import { State } from '@meraki-internal/state';
import { CareMapState } from '../../caremap/CareMapState';
import { nanoid } from 'nanoid';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';
import { IStructuredRecordDescriptor } from './StructuredRecordDescriptor';

export interface IStructuredRecordFormStateData<T> {
    record?: T;
    initialValues?: Partial<T>;
    // written but not yet read by any consumer of this base -- plumbed for the medications
    // photo-import flow, which will migrate onto this base later and needs `usedPhoto` and
    // `initialRecord` for its `Type`/`User Changed` tracking props.
    usedPhoto?: boolean;
    // for tracking, determine if the user changed the record after a photo-populated initial value
    initialRecord?: T;
}

/**
 * Generalizes MedicationFormState's upsert/delete/tracking logic so allergies, diagnoses (and
 * eventually medications) can all share one form-state implementation. A concrete subclass only
 * needs to supply `descriptor` -- everything else (init/patch/delete/updateCurrentPatient/track)
 * is keyed off it.
 */
export abstract class StructuredRecordFormState<T> extends State<IStructuredRecordFormStateData<T>> {
    protected abstract descriptor: IStructuredRecordDescriptor<T>;

    constructor(
        protected careMapState: CareMapState,
        protected mixpanel: MixPanelEventEmitter
    ) {
        super({});
    }

    init = (id: string) => {
        const patient = this.careMapState.getActiveCaremap()?.patient;
        const existing = patient && this.descriptor.getRecords(patient)?.find(r => r[this.descriptor.idField] === id);

        const record = existing || {
            ...this.descriptor.createBlank(nanoid()),
            ...this.state.initialValues
        };

        this.setState({ record, initialRecord: record });
    };

    setInitialValues = (initialValues: Partial<T>, usedPhoto?: boolean) => {
        this.setState({ initialValues, usedPhoto });
    };

    patch = (patch: Partial<T>) => {
        if (!this.state.record) {
            throw new Error('must call init before calling patch');
        }

        this.setState({
            record: {
                ...this.state.record,
                ...patch
            }
        });
    };

    delete = () => {
        if (!this.state.record) {
            throw new Error('must call init before calling delete');
        }

        this.updateCurrentPatient(true);
    };

    updateCurrentPatient = (isDelete?: boolean) => {
        const patient = this.careMapState.getActiveCaremap()?.patient;
        const record = this.state.record;

        if (!patient || !record) {
            throw new Error(`there is no current patient or ${this.descriptor.singular.toLowerCase()} to update`);
        }

        let isAdd = false;
        const copy = [ ...this.descriptor.getRecords(patient) || [] ];
        const idx = copy.findIndex(r => r[this.descriptor.idField] === record[this.descriptor.idField]);

        if (idx > -1) {
            if (isDelete) {
                this.clear();
                copy.splice(idx, 1);
            } else {
                copy.splice(idx, 1, record);
            }
        } else {
            isAdd = true;
            copy.push(record);
        }

        this.careMapState.updatePatient(this.descriptor.withRecords(patient, copy));

        this.track(isAdd, isDelete);
    };

    track = (isAdd: boolean, isDelete?: boolean) => {
        const record = this.state.record;
        if (isDelete) {
            this.mixpanel.track(`${this.descriptor.trackingNoun} Removed`);
        } else {
            this.mixpanel.track(`${this.descriptor.trackingNoun} ${isAdd ? 'Added' : 'Edited'}`, () => (
                record ? this.descriptor.buildTrackingProps(record) : {}
            ));
        }
    };

    clear = () => {
        this.setState({
            record: undefined,
            initialValues: undefined,
            usedPhoto: undefined,
            initialRecord: undefined
        });
    };
}
